// ===========================================
// PAGINA: Gestión de Usuarios (Multi-Tenant)
// ===========================================

import { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  UserPlus,
  Search,
  RefreshCw,
  Shield,
  Table as TableIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import { UsuariosList, UsuarioFormDialog, MatrizPermisos, PermisosUsuarioDialog } from '@/components/usuarios'
import type { InvitarUsuarioFormValues } from '@/components/usuarios'
import {
  useUsuariosOrg,
  useEstadisticasUsuariosOrg,
  useInvitarUsuario,
  useCambiarRolUsuario,
  useToggleBloqueoUsuario,
  useEliminarUsuarioOrg,
  useResetPassword,
} from '@/hooks/useUsuarios'
import type { RolMultiTenant } from '@/types/roles'
import { ROL_MULTITENANT_CONFIG } from '@/types/roles'
import type { FiltrosUsuarios, UsuarioConMembresia } from '@/services/usuariosService'

export function Usuarios() {
  // Estado de filtros
  const [filtros, setFiltros] = useState<FiltrosUsuarios>({})
  const [busquedaInput, setBusquedaInput] = useState('')

  // Estado de dialogs
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [usuarioPermisos, setUsuarioPermisos] = useState<UsuarioConMembresia | null>(null)

  // Queries y mutations
  const { data: usuarios = [], isLoading, refetch } = useUsuariosOrg(filtros)
  const { data: estadisticas } = useEstadisticasUsuariosOrg()
  const invitarUsuario = useInvitarUsuario()
  const cambiarRol = useCambiarRolUsuario()
  const toggleBloqueo = useToggleBloqueoUsuario()
  const eliminarUsuario = useEliminarUsuarioOrg()
  const resetPassword = useResetPassword()

  // Handlers
  const handleBuscar = () => {
    setFiltros((prev) => ({ ...prev, busqueda: busquedaInput || undefined }))
  }

  const handleNuevoUsuario = () => {
    setFormDialogOpen(true)
  }

  const handleEditarPermisos = (usuario: UsuarioConMembresia) => {
    setUsuarioPermisos(usuario)
  }

  const handleSubmitForm = async (data: InvitarUsuarioFormValues) => {
    try {
      await invitarUsuario.mutateAsync({
        email: data.email,
        name: data.nombre || undefined,
        role: data.rol,
        workspaces: data.workspaces,
      })
      toast.success(`Invitación enviada a ${data.email}`)
      setFormDialogOpen(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al invitar usuario')
    }
  }

  const handleCambiarRol = async (usuario: UsuarioConMembresia, nuevoRol: RolMultiTenant) => {
    if (!usuario.membresia_id || !usuario.rol || !usuario.updated_at) {
      toast.error('Usuario sin membresía válida')
      return
    }
    try {
      await cambiarRol.mutateAsync({
        membresiaId: usuario.membresia_id,
        nuevoRol,
        updatedAt: usuario.updated_at,
        email: usuario.email,
        rolAnterior: usuario.rol,
      })
      toast.success(`Rol cambiado a ${ROL_MULTITENANT_CONFIG[nuevoRol].label}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al cambiar rol')
    }
  }

  const handleToggleBloqueo = async (usuario: UsuarioConMembresia, bloquear: boolean) => {
    if (!usuario.auth0_user_id) {
      toast.error('Usuario sin ID de Auth0')
      return
    }
    try {
      await toggleBloqueo.mutateAsync({
        auth0UserId: usuario.auth0_user_id,
        block: bloquear,
        email: usuario.email,
      })
      toast.success(bloquear ? 'Usuario bloqueado' : 'Usuario desbloqueado')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al cambiar estado')
    }
  }

  const handleEliminarUsuario = async (usuario: UsuarioConMembresia) => {
    if (!usuario.auth0_user_id) {
      toast.error('Usuario sin ID de Auth0')
      return
    }
    try {
      await eliminarUsuario.mutateAsync({
        auth0UserId: usuario.auth0_user_id,
        email: usuario.email,
      })
      toast.success('Usuario eliminado')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al eliminar usuario')
    }
  }

  const handleResetPassword = async (usuario: UsuarioConMembresia) => {
    if (!usuario.auth0_user_id) {
      toast.error('Usuario sin ID de Auth0')
      return
    }
    try {
      await resetPassword.mutateAsync({
        auth0UserId: usuario.auth0_user_id,
        email: usuario.email,
      })
      toast.success(`Email de recuperación enviado a ${usuario.email}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al enviar reset')
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Usuarios"
        description="Gestiona los usuarios de tu organización y sus permisos"
      />

      <main className="flex-1 p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Barra de acciones */}
        <div className="flex justify-end">
          <Button onClick={handleNuevoUsuario} size="sm" className="sm:size-default">
            <UserPlus className="h-4 w-4 mr-2" />
            Invitar Usuario
          </Button>
        </div>

        {/* Estadísticas */}
        {estadisticas && (
          <div className="grid gap-3 grid-cols-2 md:gap-4 md:grid-cols-5">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{estadisticas.total}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Activos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {estadisticas.activos}
                </div>
              </CardContent>
            </Card>
            {(Object.keys(ROL_MULTITENANT_CONFIG) as RolMultiTenant[]).slice(0, 3).map((rol) => (
              <Card key={rol}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {ROL_MULTITENANT_CONFIG[rol].label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{estadisticas.porRol[rol]}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tabs: Lista y Matriz de Permisos */}
        <Tabs defaultValue="lista" className="space-y-4">
          <TabsList>
            <TabsTrigger value="lista" className="flex items-center gap-2">
              <TableIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Lista de</span> Usuarios
            </TabsTrigger>
            <TabsTrigger value="permisos" className="hidden md:flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Matriz de Permisos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lista" className="space-y-4">
            {/* Filtros */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-end">
                  {/* Búsqueda */}
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar por nombre o email..."
                        value={busquedaInput}
                        onChange={(e) => setBusquedaInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  {/* Filtro por rol */}
                  <Select
                    value={filtros.rol || 'todos'}
                    onValueChange={(value) =>
                      setFiltros((prev) => ({
                        ...prev,
                        rol: value === 'todos' ? undefined : (value as RolMultiTenant),
                      }))
                    }
                  >
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Todos los roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los roles</SelectItem>
                      {(Object.keys(ROL_MULTITENANT_CONFIG) as RolMultiTenant[]).map((rol) => (
                        <SelectItem key={rol} value={rol}>
                          {ROL_MULTITENANT_CONFIG[rol].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Filtro por estado */}
                  <Select
                    value={
                      filtros.activo === undefined
                        ? 'todos'
                        : filtros.activo
                        ? 'activos'
                        : 'inactivos'
                    }
                    onValueChange={(value) =>
                      setFiltros((prev) => ({
                        ...prev,
                        activo:
                          value === 'todos'
                            ? undefined
                            : value === 'activos',
                      }))
                    }
                  >
                    <SelectTrigger className="w-full sm:w-[140px]">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="activos">Activos</SelectItem>
                      <SelectItem value="inactivos">Inactivos</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button onClick={handleBuscar}>Buscar</Button>
                  <Button variant="outline" onClick={() => refetch()}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Lista */}
            <UsuariosList
              usuarios={usuarios}
              isLoading={isLoading}
              onEliminar={handleEliminarUsuario}
              onToggleBloqueo={handleToggleBloqueo}
              onCambiarRol={handleCambiarRol}
              onResetPassword={handleResetPassword}
              onEditarPermisos={handleEditarPermisos}
            />
          </TabsContent>

          <TabsContent value="permisos">
            <Card>
              <CardHeader>
                <CardTitle>Matriz de Permisos</CardTitle>
                <CardDescription>
                  Visualiza los permisos disponibles para cada rol del sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MatrizPermisos />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialogs */}
      <UsuarioFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        onSubmit={handleSubmitForm}
        isLoading={invitarUsuario.isPending}
      />

      <PermisosUsuarioDialog
        usuario={usuarioPermisos}
        open={!!usuarioPermisos}
        onOpenChange={(o) => !o && setUsuarioPermisos(null)}
      />
    </div>
  )
}
